import React from 'react';
import { Cookie, Settings, Shield, Eye, Database, Users, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookiePolicyPage: React.FC = () => {
  const lastUpdated = 'December 15, 2024';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-orange-100 rounded-full">
              <Cookie className="h-8 w-8 text-orange-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Cookie Policy</h1>
          <p className="text-lg text-gray-600">
            Understanding how we use cookies to improve your experience
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="prose prose-lg max-w-none">
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Cookie className="h-6 w-6 text-orange-600 mr-3" />
                What Are Cookies?
              </h2>
              <p className="text-gray-700 mb-4">
                Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences, analyzing how you use our site, and personalizing content.
              </p>
              <p className="text-gray-700">
                Cookies are essential for the proper functioning of our platform and help us provide you with the best possible service.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Settings className="h-6 w-6 text-orange-600 mr-3" />
                How We Use Cookies
              </h2>
              <p className="text-gray-700 mb-4">
                We use cookies for several purposes to enhance your experience on our platform:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <Shield className="h-5 w-5 text-blue-600 mr-2" />
                    Essential Cookies
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• User authentication and security</li>
                    <li>• Session management</li>
                    <li>• Basic platform functionality</li>
                    <li>• Form submissions and validation</li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <Eye className="h-5 w-5 text-green-600 mr-2" />
                    Analytics Cookies
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Website usage statistics</li>
                    <li>• Performance monitoring</li>
                    <li>• User behavior analysis</li>
                    <li>• Service improvement insights</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <Users className="h-5 w-5 text-yellow-600 mr-2" />
                    Functional Cookies
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• User preferences and settings</li>
                    <li>• Language and region selection</li>
                    <li>• Personalized content delivery</li>
                    <li>• Enhanced user experience</li>
                  </ul>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <Database className="h-5 w-5 text-purple-600 mr-2" />
                    Third-Party Cookies
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Payment processing services</li>
                    <li>• Social media integration</li>
                    <li>• Advertising and marketing</li>
                    <li>• External service providers</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Types of Cookies We Use</h2>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">Session Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies are temporary and are deleted when you close your browser. They are essential for the proper functioning of our platform and help maintain your session while you navigate through our website.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">Persistent Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies remain on your device for a set period or until you delete them. They help us remember your preferences and provide a personalized experience across multiple visits.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">Third-Party Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies are set by external services that we use to enhance our platform, such as analytics providers, payment processors, and social media platforms.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Specific Cookies We Use</h2>
              
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">Cookie Name</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">Purpose</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">session_id</td>
                      <td className="px-4 py-3 text-sm text-gray-700">User authentication and session management</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Session</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">user_preferences</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Store user settings and preferences</td>
                      <td className="px-4 py-3 text-sm text-gray-700">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">analytics_id</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Website usage analytics and performance</td>
                      <td className="px-4 py-3 text-sm text-gray-700">2 years</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">marketing_consent</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Track marketing communication preferences</td>
                      <td className="px-4 py-3 text-sm text-gray-700">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Services</h2>
              <p className="text-gray-700 mb-4">
                We use the following third-party services that may set cookies on your device:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Google Analytics:</strong> Website analytics and performance monitoring</li>
                <li><strong>Stripe:</strong> Payment processing and security</li>
                <li><strong>Facebook Pixel:</strong> Marketing and advertising optimization</li>
                <li><strong>Intercom:</strong> Customer support and communication</li>
                <li><strong>Hotjar:</strong> User experience research and optimization</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Managing Your Cookie Preferences</h2>
              <p className="text-gray-700 mb-4">
                You have several options for managing cookies on our platform:
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="text-lg font-medium text-blue-800 mb-2">Browser Settings</h4>
                <p className="text-blue-700 text-sm">
                  Most web browsers allow you to control cookies through their settings. You can:
                </p>
                <ul className="text-blue-700 text-sm mt-2 space-y-1">
                  <li>• Block all cookies</li>
                  <li>• Block third-party cookies only</li>
                  <li>• Delete existing cookies</li>
                  <li>• Set preferences for specific websites</li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h4 className="text-lg font-medium text-green-800 mb-2">Cookie Consent Manager</h4>
                <p className="text-green-700 text-sm">
                  Use our cookie consent manager to customize your preferences for different types of cookies.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="text-lg font-medium text-yellow-800 mb-2">Opt-Out Links</h4>
                <p className="text-yellow-700 text-sm">
                  Some third-party services provide opt-out mechanisms for their cookies and tracking.
                </p>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Impact of Disabling Cookies</h2>
              <p className="text-gray-700 mb-4">
                While you can disable cookies, please note that doing so may affect the functionality of our platform:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Some features may not work properly</li>
                <li>You may need to re-enter information more frequently</li>
                <li>Personalized content may not be available</li>
                <li>Security features may be compromised</li>
                <li>Performance monitoring may be limited</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Updates to This Policy</h2>
              <p className="text-gray-700">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website and updating the "Last Updated" date.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-gray-700">
                  <strong>Email:</strong> privacy@therapistdirect.com
                </p>
                <p className="text-gray-700">
                  <strong>Phone:</strong> 1-800-WELLNESS
                </p>
                <p className="text-gray-700">
                  <strong>Address:</strong> 123 Wellness St, Suite 400, Mental Health City, MH 12345
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <p className="text-sm text-gray-500">
                  © {new Date().getFullYear()} TherapistDirect. All rights reserved.
                </p>
                <div className="flex space-x-6 text-sm">
                  <Link to="/privacy" className="text-orange-600 hover:text-orange-800">
                    Privacy Policy
                  </Link>
                  <Link to="/terms" className="text-orange-600 hover:text-orange-800">
                    Terms of Service
                  </Link>
                  <Link to="/accessibility" className="text-orange-600 hover:text-orange-800">
                    Accessibility
                  </Link>
                  <Link to="/" className="text-orange-600 hover:text-orange-800">
                    Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
